import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ConfirmProvider } from 'material-ui-confirm';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import PageLoading from './components/common/PageLoading.jsx';
import Layout from './components/Layout.jsx';
import AppContent from './components/routing/AppContent.jsx';
import { CommissionsPermissionsProvider } from './contexts/CommissionsPermissionsContext.jsx';
import { PermissionsProvider } from './contexts/PermissionsContext.jsx';
import useAuth0Roles from './hooks/auth0Roles.jsx';
import useCustomTheme from './hooks/customizeReactTheme.jsx';
import { addAccessTokenInterceptor } from './axiosClient.js';

function App() {
  const isAlphaVersion = window.globalConfig.alphaMode || false;
  const { isLoading, isAuthenticated, error, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();
  const { roles, rolesValid } = useAuth0Roles();

  const theme = useCustomTheme();
  const [alphaRoleError, setAlphaRoleError] = React.useState(false);

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
      });
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect]);

  React.useEffect(() => {
    if (isAlphaVersion && isAuthenticated && rolesValid) {
      if (!roles.includes('Commissions Alpha')) {
        setAlphaRoleError(true);
      } else {
        setAlphaRoleError(false);
      }
    } else if (!isAlphaVersion) {
      setAlphaRoleError(false);
    }
  }, [isAlphaVersion, isAuthenticated, roles, rolesValid]);

  const handleClick = React.useCallback(
    () =>
      loginWithRedirect({
        appState: { returnTo: '/' },
      }),
    [loginWithRedirect]
  );

  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[App] Adding axios interceptor with getAccessTokenSilently');
    addAccessTokenInterceptor(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <ConfirmProvider>
          <Box>
            {error && (
              <Container maxWidth="lg">
                <div style={{ paddingBottom: '0.2rem' }}>Oops... {error.message}</div>
                <Button variant="contained" onClick={handleClick}>
                  Login
                </Button>
              </Container>
            )}
            {alphaRoleError && (
              <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="h6">Permission Denied</Typography>
                  <Typography>
                    You are using an alpha version of the application but do not have the required
                    &quot;Commissions Alpha&quot; role. Please contact your administrator for access
                    or use the production version.
                  </Typography>
                </Alert>
                <Button variant="contained" onClick={handleClick}>
                  Login with Different Account
                </Button>
              </Container>
            )}
            {!error && !alphaRoleError && (isLoading || !isAuthenticated) && <PageLoading />}
            {isAuthenticated && !alphaRoleError && (
              <PermissionsProvider>
                <CommissionsPermissionsProvider>
                  <Layout>
                    <AppContent />
                  </Layout>
                </CommissionsPermissionsProvider>
              </PermissionsProvider>
            )}
          </Box>
        </ConfirmProvider>
      </ThemeProvider>
    </div>
  );
}
export default App;
